import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Typography,
} from "@mui/material";
import { formatDateTime, roundToX } from "../../common/common";
import { loadCaptureScans } from "../../reducer/RawSurfaceSlice";
import { tokens } from "../../theme";

const MICRON_TO_INCH = 0.00003937;

/**
 * Wider than MUI's "lg" (1200px) by 150px, to fit more of the table without horizontal scrolling.
 * Bump this rather than changing the Dialog's maxWidth breakpoint, which only moves in fixed steps.
 * The table still scrolls inside its own container if the viewport is narrower than this.
 */
const DIALOG_MAX_WIDTH = 1350;

/**
 * Height of the group header row, needed as an explicit number rather than left to content: the
 * column-header row sticks to `top: GROUP_ROW_HEIGHT`, and without a fixed height for the row above
 * it the two would overlap. MUI's stickyHeader pins every header cell to top: 0 by default, which
 * only works for a single header row.
 */
const GROUP_ROW_HEIGHT = 28;

/** Caps the table rather than the dialog, so the header stays put and the caption below it does too. */
const TABLE_MAX_HEIGHT = "60vh";

/** Same conversion and 3-decimal presentation as ShimsCard/RawSurfaceSaveDialog. */
const shimInches = (microns) =>
    typeof microns === "number" ? roundToX(microns * MICRON_TO_INCH, 3).toFixed(3) : "—";

/**
 * A disabled gate reads as "off" rather than showing its stored number: the value is retained so
 * re-enabling restores it, but displaying it would imply a threshold that never ran.
 */
const gate = (enabled, value, suffix = "") => (enabled ? `${value}${suffix}` : "off");

/**
 * A shaft span as "start–stop". Renders as a dash when the save predates these columns, or when
 * the range was left to the API's configured default - in both cases no span was actually chosen,
 * so showing a number would misrepresent what produced the shim.
 */
const range = (start, stop) =>
    typeof start === "number" && typeof stop === "number"
        ? `${Math.round(start).toLocaleString()}–${Math.round(stop).toLocaleString()}`
        : "—";

/**
 * Compares the scans saved from one raw capture.
 *
 * The point of tracking saves was never the list itself - it was being able to put the settings
 * that produced each scan next to the shim values that resulted, so the effect of a threshold
 * change is readable directly. Hence one row per save, thresholds and shims on the same row.
 *
 * Read-only by design: this pass is the comparison view, not an editor. Rows are ordered newest
 * first, matching the order the endpoint returns.
 */
export default function RawSurfaceHistoryDialog({ open, onClose }) {
    const { savedScans, savedScansStatus, fileName, captureId } = useSelector((state) => state.rawSurface);
    const dispatch = useDispatch();

    // Refetched on every open, not just when the capture loads. A scan can be deleted from the
    // Scan Analysis tab, which knows nothing about this list, so anything cached here may already
    // describe scans that no longer exist. The database side is handled by the source row's
    // cascade FK; this is purely about not showing a stale copy of it.
    useEffect(() => {
        if (open && captureId) {
            dispatch(loadCaptureScans({ captureId }));
        }
    }, [open, captureId, dispatch]);

    // Padding is tightened from MUI's small-table default (6px 16px) and units are hoisted into
    // group headers rather than repeated per cell. Both exist to keep twelve columns inside the
    // dialog without a horizontal scroll, which is what adding the shaft ranges pushed it past.
    const headCellSx = {
        fontWeight: 600, whiteSpace: "nowrap", color: tokens.muted, fontSize: "11.5px",
        padding: "6px 8px",
        // Explicit background: a sticky cell is transparent by default, so body rows would show
        // through it as they scroll underneath.
        backgroundColor: tokens.surface,
        top: GROUP_ROW_HEIGHT,
    };
    // No bottom border anywhere on the group row - including its two blank cells, which would
    // otherwise keep MUI's default and draw a stray rule above "Saved"/"Description". The column
    // headers below already separate the head from the body.
    const groupHeadCellSx = {
        ...headCellSx,
        borderBottom: "none",
        top: 0,
        height: GROUP_ROW_HEIGHT,
    };
    const cellSx = { whiteSpace: "nowrap", padding: "6px 8px" };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            fullWidth
            PaperProps={{ sx: { maxWidth: DIALOG_MAX_WIDTH } }}
        >
            <DialogTitle>Scans saved from this capture</DialogTitle>
            <DialogContent>
                {fileName && (
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
                        {fileName}
                    </Typography>
                )}

                {savedScansStatus === "error" && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Could not load the saved scans for this capture.
                    </Alert>
                )}

                {savedScans.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No scans have been saved from this capture yet.
                    </Typography>
                ) : (
                    // Scrolls inside its own container on both axes, so the dialog itself never
                    // scrolls: sideways if the viewport is narrower than the table, and vertically
                    // once enough scans have been saved. Capping the container rather than letting
                    // DialogContent scroll is what keeps the header and the caption in place.
                    <TableContainer sx={{ overflowX: "auto", overflowY: "auto", maxHeight: TABLE_MAX_HEIGHT }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={groupHeadCellSx} />
                                    <TableCell sx={groupHeadCellSx} />
                                    <TableCell sx={groupHeadCellSx} align="center" colSpan={2}>
                                        Shaft range (µm)
                                    </TableCell>
                                    <TableCell sx={groupHeadCellSx} align="center" colSpan={4}>
                                        Thresholds
                                    </TableCell>
                                    <TableCell sx={groupHeadCellSx} align="center" colSpan={4}>
                                        Shim (inches)
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={headCellSx}>Saved</TableCell>
                                    <TableCell sx={headCellSx}>Description</TableCell>
                                    <TableCell sx={headCellSx} align="right">Machine</TableCell>
                                    <TableCell sx={headCellSx} align="right">Motor</TableCell>
                                    <TableCell sx={headCellSx} align="right">Fix</TableCell>
                                    <TableCell sx={headCellSx} align="right">Angle</TableCell>
                                    <TableCell sx={headCellSx} align="right">Points</TableCell>
                                    <TableCell sx={headCellSx} align="right">Sigma</TableCell>
                                    <TableCell sx={headCellSx} align="right">Front V</TableCell>
                                    <TableCell sx={headCellSx} align="right">Front H</TableCell>
                                    <TableCell sx={headCellSx} align="right">Rear V</TableCell>
                                    <TableCell sx={headCellSx} align="right">Rear H</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {savedScans.map((entry) => (
                                    // The scan id has no column of its own - it matters to too few
                                    // people to spend width on - but stays reachable on hover for
                                    // the occasions it is needed.
                                    // Year is dropped from the column and kept in the tooltip, along
                                    // with the scan id: within one capture's history the saves are
                                    // days apart at most, so the year only costs width.
                                    <TableRow
                                        key={entry.scanId}
                                        hover
                                        title={`Scan ${entry.scanId} · ${formatDateTime(entry.savedAt)}`}
                                    >
                                        <TableCell sx={cellSx}>{formatDateTime(entry.savedAt, true)}</TableCell>
                                        <TableCell sx={{ ...cellSx, maxWidth: 200, whiteSpace: "normal" }}>
                                            <Typography noWrap variant="body2">{entry.description}</Typography>
                                        </TableCell>
                                        <TableCell sx={cellSx} align="right">
                                            {range(entry.machineStart, entry.machineStop)}
                                        </TableCell>
                                        <TableCell sx={cellSx} align="right">
                                            {range(entry.motorStart, entry.motorStop)}
                                        </TableCell>
                                        <TableCell sx={cellSx} align="right">
                                            {gate(entry.circleFixEnabled, entry.circleFixLimit, " µm")}
                                        </TableCell>
                                        <TableCell sx={cellSx} align="right">
                                            {gate(entry.angleEnabled, entry.angleLimit, "°")}
                                        </TableCell>
                                        <TableCell sx={cellSx} align="right">
                                            {gate(entry.pointEnabled, entry.pointLimit)}
                                        </TableCell>
                                        <TableCell sx={cellSx} align="right">
                                            {gate(entry.sigmaEnabled, entry.maxSigma)}
                                        </TableCell>
                                        <TableCell sx={cellSx} align="right">{shimInches(entry.frontVertical)}</TableCell>
                                        <TableCell sx={cellSx} align="right">{shimInches(entry.frontHorizontal)}</TableCell>
                                        <TableCell sx={cellSx} align="right">{shimInches(entry.rearVertical)}</TableCell>
                                        <TableCell sx={cellSx} align="right">{shimInches(entry.rearHorizontal)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                        A dash means the value was not recorded for that save. Hover a row for its
                        scan id and full timestamp.
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button color="inherit" onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
